/**
 * 申請の詳細ポップアップ(v3.8)。
 * - 項目の並び・構成は経費申請画面と同一(3章): 申請者/会社/事業部/拠点・場所/科目①②/
 *   計上日/支払い予定金額/支払い予定日/支払い方法/支払い者/添付/支払先/摘要/備考
 * - 進捗の帯・差戻し日時(赤)・差戻しコメント履歴(誰が・いつ・何と)を表示(4章)
 * - 経理・管理者は「経理修正」で会社・事業部・拠点・科目を後から付け替えられる
 *   (「その他」で申請された分の振替用。完了後は不可)
 * - 承認者は「修正(全項目)」でどの段階でも全項目を直せる(2026-09-17 発注者指示。完了後も可)
 * - `actions` を渡さなければ閲覧専用
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import Modal from '@core/components/Modal';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { fetchUsers } from '@core/masters/masterRepo';
import { fetchHierarchyNodes, toPathLabel, childrenOf } from '@core/masters/hierarchyRepo';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { formatCashAccountLabel } from '@core/lib/cashAccountLabel';
import AttachmentThumbs from '@core/expense/AttachmentThumbs';
import FileDropZone from '@core/expense/FileDropZone';
import { uploadFiles, validateFile } from '@core/expense/attachments';
import { RECURRING_MANAGER_CHECK_FROM_MS, reportAccountingConfirm, reportApproverConfirm } from './flowActions';
import { isFlowStaleError, reportFlowResult } from './flowResult';
import { diffEdit, fetchEditHistory, saveTransactionEdit, type EditChange, type EditField, type EditHistoryEntry } from './editHistory';
import { saveApproverEdit } from './approverEdit';
import { fetchFlowPatterns, findPattern, type FlowPattern } from './flowPatterns';
import { roleOf } from './applicationRepo';
import DateField from '@core/components/DateField';
import { StatusBadge } from './FlowParts';
import { fiscalYearOf, toShortDate, tsToDateLabel, tsToMillis, tsToShortDate } from '@core/lib/date';
import { useIsMobile } from '@core/lib/useIsMobile';
import { formatAmountInput, formatYen, parseAmount } from '@core/lib/money';
import {
  APPLY_PAYMENT_METHODS,
  COST_TYPES,
  COST_TYPE_LABELS,
  PAYER_LABELS,
  PAYMENT_METHOD_LABELS,
  isFixedCost,
  type CashAccount,
  type CashAccountKind,
  type CostType,
  type HierarchyNode,
  type Payer,
  type PaymentMethod,
  type Transaction,
  type User,
} from '@core/lib/types';
import ExternalDetail, { externalOriginLabel } from '@core/routine/ExternalDetail';

/** 口座マスタの種別 → 支払方法区分(支払いモーダルと同じ対応) */
function methodOfKind(kind: CashAccountKind): PaymentMethod {
  return kind === 'cash' ? 'cash' : kind === 'card' ? 'card' : 'bank_transfer';
}

/** ラベル+値の1行。値は折り返して読めるようにする。
    ラベル列は固定幅(2026-08-31 指示): 長いラベル(支払い予定金額(税込み)・
    領収書/振込明細/請求書)でも値の開始位置が揃うようにする */
/** stack=true は見出しを上、中身を全幅にする(添付のサムネイルなど幅が要るもの。2026-10-14) */
function Item({ label, children, stack }: { label: string; children: ReactNode; stack?: boolean }) {
  return (
    <div className={stack ? 'detail-item detail-item-stack' : 'detail-item'}>
      <div className="hint" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
        {label}
      </div>
      <div style={{ fontSize: 13.5, minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.06em', marginBottom: 6 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

/* 2カラム表示はCSSクラス .two-col に移行(2026-08-28): スマホでは1カラムに落として
   「支払い予定金額(税込み)」等のラベルが折り返さないようにする(layout.css) */

export default function ApplicationDetailModal({
  transaction: t,
  onClose,
  actions,
  onChanged,
}: {
  transaction: Transaction;
  onClose: () => void;
  /** 権限・段階に応じた操作ボタン(呼び出し側が指定)。省略時は閲覧のみ */
  actions?: ReactNode;
  /** 経理修正で内容が変わったときに呼ばれる(一覧の再読込用) */
  onChanged?: () => void;
}) {
  const { user, canConfirm, can, flowRole } = useAuth();
  const {
    companies,
    departments,
    companyName,
    departmentName,
    accountName,
    subAccountName,
    expenseAccounts,
    subAccountsOf,
    accounts,
    fiscal,
  } = useMasters();
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  /** 申請者の役割(支払い方法×支払い者の組合せの確認に使う) */
  const [users, setUsers] = useState<User[]>([]);
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  const [fixing, setFixing] = useState(false);
  const [busy, setBusy] = useState(false);

  // 経理修正フォームの状態
  const [fixCompany, setFixCompany] = useState(t.company_id ?? 'sum');
  const [fixDept, setFixDept] = useState<string>(t.department_id);
  const [fixNode, setFixNode] = useState(t.hierarchy_node_id ?? '');
  const [fixAccount, setFixAccount] = useState(t.account_id);
  const [fixSub, setFixSub] = useState(t.sub_account_id);
  const [fixNodes, setFixNodes] = useState<HierarchyNode[]>([]);
  // 承認者の全項目修正(2026-08-26 会議で支払い済みのみ → 2026-09-17 からどの段階でも): 金額・日付・明細も直せる
  const [fixAmount, setFixAmount] = useState(formatAmountInput(String(t.amount)));
  const [fixAppliedAmount, setFixAppliedAmount] = useState(formatAmountInput(String(t.applied_amount ?? t.amount)));
  const [fixMethod, setFixMethod] = useState<PaymentMethod | ''>(t.payment_method ?? '');
  const [fixPayer, setFixPayer] = useState<Exclude<Payer, null> | ''>(t.payer ?? '');
  const [fixPaymentAccountId, setFixPaymentAccountId] = useState(t.payment_account_id ?? '');
  /** 振込手数料。空欄=未入力(null)、0=0円(支払いモーダルと同じ区別) */
  const [fixFee, setFixFee] = useState(t.transfer_fee == null ? '' : formatAmountInput(String(t.transfer_fee)));
  const [fixPurchaseDate, setFixPurchaseDate] = useState(t.purchase_date ?? '');
  /** 固定費・変動費(2026-09-24 指示)。承認者だけが切り替えられる */
  const [fixCostType, setFixCostType] = useState<CostType>(isFixedCost(t) ? 'fixed' : 'variable');
  /** 追加する添付(領収書/振込明細/請求書) */
  const [fixFiles, setFixFiles] = useState<File[]>([]);
  const [patterns, setPatterns] = useState<FlowPattern[]>([]);
  const [fixPostingDate, setFixPostingDate] = useState(t.posting_date);
  const [fixPaymentDate, setFixPaymentDate] = useState(t.payment_date);
  const [fixCounterparty, setFixCounterparty] = useState(t.counterparty ?? '');
  const [fixDescription, setFixDescription] = useState(t.description ?? '');
  const [fixNote, setFixNote] = useState(t.note ?? '');
  /** 承認者備考(2026-08-27 新設)。完了前の修正フォーム最下部で入力し、詳細に表示する */
  const [fixApproverNote, setFixApproverNote] = useState(t.approver_note ?? '');
  /** 修正履歴(2026-09-16 改修依頼 C)。読めるのは承認者・管理者のみ(権限ルール) */
  const canSeeEditHistory = can('master_admin');
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);

  useEffect(() => {
    if (!canSeeEditHistory) return;
    let alive = true;
    fetchEditHistory(t.id)
      .then((list) => alive && setEditHistory(list))
      .catch(() => alive && setEditHistory([]));
    return () => {
      alive = false;
    };
  }, [t.id, canSeeEditHistory]);

  useEffect(() => {
    fetchUsers()
      .then((list) => {
        setUsers(list);
        setUserNames(new Map(list.map((u) => [u.id, u.display_name])));
      })
      .catch(() => undefined);
    fetchHierarchyNodes(t.department_id)
      .then(setNodes)
      .catch(() => setNodes([]));
    fetchCashAccounts()
      .then(setCashAccounts)
      .catch(() => setCashAccounts([]));
  }, [t.department_id]);

  useEffect(() => {
    if (!fixing) return;
    fetchHierarchyNodes(fixDept)
      .then(setFixNodes)
      .catch(() => setFixNodes([]));
  }, [fixing, fixDept]);

  useEffect(() => {
    if (!fixing) return;
    fetchFlowPatterns()
      .then(setPatterns)
      .catch(() => setPatterns([]));
  }, [fixing]);

  const name = (id: string | null | undefined) => (id ? (userNames.get(id) ?? '—') : '—');
  const node = t.hierarchy_node_id ? nodes.find((n) => n.id === t.hierarchy_node_id) : null;
  const paymentAccountObj = t.payment_account_id ? cashAccounts.find((a) => a.id === t.payment_account_id) : undefined;
  // 表記は「会社名: 口座名」(2026-09-08 指示)
  const paymentAccount = t.payment_account_id
    ? paymentAccountObj
      ? formatCashAccountLabel(paymentAccountObj, companyName(paymentAccountObj.company_id))
      : '—'
    : null;
  /** 立替(advance)の分は、経理の精算と受領の記録が済んでいるか(完了の条件。firestore.rules の reimbursedIfAdvance と同じ) */
  const reportSettled = t.advance !== true || (t.reimbursed_at != null && t.settlement_received_at != null);
  /**
   * 承認者の全項目修正(2026-08-26 会議で支払い済みのみ → 2026-09-17 発注者指示でどの段階でも・完了後も)。
   * firestore.rules の approverEditAction と同じ条件(承認者・管理者)
   */
  const canFullFix = flowRole === 'approver' || can('master_admin');
  /** 経理修正はロック(完了)前まで。経理または管理者 */
  const canFix = canFullFix || (canConfirm && t.status !== 'completed');
  const isPaid = !!t.paid_at;
  /**
   * 支払い者・支払い方法は、お金が動く前(仮払い・支払いの前)だけ変えられる [仮置き 2026-09-17]。
   * 支払い後に変えると立替精算の要否などお金の流れが崩れるため、変える場合は差戻しで対応する
   */
  const moneyMoved = isPaid || !!t.cash_handed_at || ['cash_ready', 'received', 'paid', 'completed'].includes(t.status);
  /** 支払い方法・支払い者を持つのは申請(経費・請求書)だけ。支払い済み申請(報告)・繰り返し支払いは変えない */
  const methodEditable = !!t.doc_type && t.source !== 'report' && t.source !== 'recurring';
  const canChangeMethod = methodEditable && !moneyMoved;
  /** 会社の口座を使わない支払い(仮払い・立替)は口座を持たない */
  const noCompanyAccount = t.payment_method === 'prepaid' || t.payment_method === 'reimbursement' || t.advance === true;
  const showAccountField = isPaid && !noCompanyAccount && !!t.payment_account_id;
  /** 振込手数料は経理の振込のときだけ(支払いモーダルと同じ条件) */
  const showFeeField = isPaid && !noCompanyAccount && t.payer === 'accounting' && t.payment_method === 'bank_transfer';
  const showPurchaseField = isPaid && t.payment_method === 'card' && !!t.purchase_date;
  const feeAccount = accounts.find((a) => a.active && a.name === '支払手数料') ?? null;
  /** 支払い口座の候補: 申請の会社(「その他」は全社)で、支払い方法と同じ種別の口座。今の口座は常に含める */
  const accountOptions = cashAccounts.filter(
    (a) =>
      a.id === t.payment_account_id ||
      (a.active &&
        (fixCompany === 'sonota' || a.company_id === fixCompany) &&
        (t.payment_method == null ||
          methodOfKind(a.kind) === t.payment_method ||
          (t.payment_method === 'direct_debit' && a.kind === 'bank'))),
  );

  // 報告型(支払い済み申請・承認不要)。通常フローと同じ画面で進捗・確認できる(2026-08-27)
  const isReport = t.source === 'report';
  /** 繰り返し支払い(2026-09-07): 申請なしに自動で作られ、経理の支払い→部門長確認(2026-10-15)→承認者の完了確認で進む */
  const isRecurring = t.source === 'recurring';
  // 仮払いは 経理の仮払い→本人の受領(受領書添付) を経由する(v1.1 §3-C)
  const prepaidFlow = t.payment_method === 'prepaid' || !!t.cash_handed_at;
  const steps: Array<{ label: string; at: unknown; by: string | null | undefined }> = isRecurring
    ? [
        { label: '支払い待ち(自動作成)', at: t.created_at, by: null },
        { label: '支払い(経理)', at: t.paid_at, by: t.paid_by },
        // 部門長確認(2026-10-15 追加): 経理の支払いのあと、事業部の部門長が確認する。
        // 追加前に支払った分・部門長がいない事業部で完了した分は、この段を出さない
        ...(t.manager_checked_at ||
        (t.status !== 'completed' && (!t.paid_at || tsToMillis(t.paid_at) >= RECURRING_MANAGER_CHECK_FROM_MS))
          ? [{ label: '部門長確認', at: t.manager_checked_at, by: t.manager_checked_by }]
          : []),
        { label: '完了確認', at: t.completed_at, by: t.completed_by },
      ]
    : isReport
    ? [
        { label: '報告', at: t.applied_at, by: t.created_by },
        { label: '経理確認', at: t.report_accounting_confirmed_at, by: t.report_accounting_confirmed_by },
        { label: '承認者確認(完了)', at: t.completed_at, by: t.completed_by },
      ]
    : [
        { label: '申請', at: t.applied_at, by: t.created_by },
        { label: '部門長確認', at: t.manager_checked_at, by: t.manager_checked_by },
        { label: '承認', at: t.approved_at, by: t.approved_by },
        ...(prepaidFlow
          ? [
              { label: '仮払い(経理)', at: t.cash_handed_at, by: t.cash_handed_by },
              { label: '受領', at: t.received_at, by: t.received_by },
            ]
          : []),
        { label: '支払い', at: t.paid_at, by: t.paid_by },
        ...(t.advance
          ? [
              { label: '立替精算', at: t.reimbursed_at, by: t.reimbursed_by },
              { label: '受領書添付', at: t.settlement_received_at, by: t.settlement_received_by },
            ]
          : []),
        { label: '完了', at: t.completed_at, by: t.completed_by },
      ];

  /** 報告型の確認操作(どの画面の詳細からでも行える・2026-08-27) */
  const canReportAccounting = isReport && t.status === 'paid' && !t.report_accounting_confirmed_at
    && (flowRole === 'accounting' || can('master_admin'));
  const canReportApprover = isReport && t.status === 'paid'
    && (flowRole === 'approver' || can('master_admin'));

  async function runReportConfirm(kind: 'accounting' | 'approver') {
    if (!user) return;
    setBusy(true);
    try {
      if (kind === 'accounting') {
        const result = await reportAccountingConfirm({ transaction: t, userId: user.id });
        reportFlowResult(toast, `${t.code} を経理確認しました`, result);
      } else {
        const result = await reportApproverConfirm({ transaction: t, userId: user.id });
        reportFlowResult(toast, `${t.code} を承認者確認しました(完了)`, result);
      }
      onChanged?.();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '確認に失敗しました');
      // ほかの人が先に確認していた場合は、一覧を最新にして閉じる
      if (isFlowStaleError(e)) {
        onChanged?.();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  // ヘッダーの「→ ◯◯画面へ」ボタンは廃止(2026-08-27 指示。お知らせクリックで
  // 該当ページの詳細が自動で開くため不要)

  const rejectHistory = [...(t.reject_history ?? [])].reverse();

  async function saveFix() {
    if (canFullFix) {
      await saveFullFix();
      return;
    }
    setBusy(true);
    try {
      // 経理修正: 会社・事業部・拠点・科目のみ(rules: accountingFixAction)
      const values: Partial<Record<EditField, string | number | null>> = {
        company_id: fixCompany,
        department_id: fixDept,
        hierarchy_node_id: fixNode || null,
        account_id: fixAccount,
        sub_account_id: fixSub || '',
      };
      // 何を何に変えたかを、保存時点の名前(科目名・事業部名など)で残す(2026-09-16 改修依頼 C)
      const changes = diffEdit(t as unknown as Record<string, unknown>, values, editValueText);
      const { historySaved } = await saveTransactionEdit({
        transactionId: t.id,
        patch: {
          ...values,
          updated_at: serverTimestamp(),
          updated_by: user?.id ?? '',
        },
        changes,
        userId: user?.id ?? '',
        action: 'accounting-fix',
      });
      if (!historySaved) {
        // 修正そのものは保存済み。履歴だけ残らなかったことを必ず知らせる
        window.alert('修正は保存しましたが、修正履歴の記録に失敗しました。お手数ですが、変更した内容を管理者に伝えてください。');
      }
      toast('経理修正を保存しました(会社・事業部・拠点・科目)');
      setFixing(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /**
   * 承認者の全項目修正の保存(2026-09-17)。変わった項目だけを書き、支払い後の金額・支払日・口座・振込手数料の変更は
   * 資金繰り(CF)の出金と手数料の取引にも反映する(approverEdit.ts)
   */
  async function saveFullFix() {
    if (!user) return;
    const appliedAmount = parseAmount(fixAppliedAmount);
    // 支払い前は「支払い予定金額」が金額そのもの。支払い後は確定金額を別に持つ
    const amount = isPaid ? parseAmount(fixAmount) : appliedAmount;
    if (appliedAmount === null || amount === null) {
      toast('金額は1円以上の整数で入力してください');
      return;
    }
    if (!fixPostingDate || !fixPaymentDate) {
      toast(isPaid ? '計上日と支払日を入力してください' : '計上日と支払い予定日を入力してください');
      return;
    }
    const feeDigits = fixFee.replace(/[^0-9]/g, '');
    const fee = feeDigits === '' ? null : Number(feeDigits);
    for (const f of fixFiles) {
      const err = validateFile(f);
      if (err) {
        toast(err);
        return;
      }
    }
    const applicant = users.find((u) => u.id === t.created_by);
    const methodChanged = canChangeMethod && (fixMethod !== (t.payment_method ?? '') || fixPayer !== (t.payer ?? ''));
    if (methodChanged) {
      if (!fixMethod || !fixPayer) {
        toast('支払い方法と支払い者を選んでください');
        return;
      }
      if (applicant && patterns.length > 0 && !findPattern(patterns, roleOf(applicant), fixMethod, fixPayer)) {
        toast('この支払い方法と支払い者の組合せは選べません(申請フロー仕様§2)');
        return;
      }
      // 経費申請と同じ添付の条件(振込=請求書必須 / 経理に依頼=必須。現金は任意)
      const attachmentCount = (t.attachments ?? []).length + fixFiles.length;
      if (attachmentCount === 0 && fixMethod === 'bank_transfer') {
        toast('「振込」は請求書の添付が必要です。「添付を追加」から添付してください');
        return;
      }
      if (attachmentCount === 0 && fixPayer === 'accounting' && fixMethod !== 'cash') {
        toast('「経理に依頼する」の場合は請求書または振込明細の添付が必要です。「添付を追加」から添付してください');
        return;
      }
    }

    const form: Partial<Record<EditField, string | number | null>> = {
      company_id: fixCompany,
      department_id: fixDept,
      hierarchy_node_id: fixNode || null,
      account_id: fixAccount,
      sub_account_id: fixSub || '',
      posting_date: fixPostingDate,
      applied_amount: appliedAmount,
      amount,
      payment_date: fixPaymentDate,
      ...(showPurchaseField ? { purchase_date: fixPurchaseDate || null } : {}),
      ...(canChangeMethod ? { payment_method: fixMethod || null, payer: fixPayer || null } : {}),
      ...(showAccountField ? { payment_account_id: fixPaymentAccountId || null } : {}),
      ...(showFeeField ? { transfer_fee: fee } : {}),
      cost_type: fixCostType,
      counterparty: fixCounterparty.trim() || null,
      description: fixDescription.trim() || null,
      note: fixNote.trim() || null,
      approver_note: fixApproverNote.trim() || null,
    };
    // 固定/変動を切り替えていない取引は、いまの自動判定を「変更前」として履歴に残す(「(空欄)→固定」にしない)
    const before = { ...(t as unknown as Record<string, unknown>), cost_type: t.cost_type ?? (isFixedCost(t) ? 'fixed' : 'variable') };
    // 支払い前は金額と支払日が「予定」なので、履歴の名前もそれに合わせる
    const diff = diffEdit(
      before,
      form,
      editValueText,
      isPaid ? {} : { amount: '支払い予定金額', payment_date: '支払い予定日' },
    );
    // 支払い前は金額と支払い予定金額が同じ値のため、履歴には1行だけ残す
    const changes: EditChange[] = isPaid
      ? diff
      : diff.filter((c) => !(c.field === 'applied_amount' && diff.some((x) => x.field === 'amount')));
    if (diff.length === 0 && fixFiles.length === 0) {
      toast('変更された項目がありません');
      return;
    }

    setBusy(true);
    try {
      const patch: Record<string, unknown> = {};
      for (const c of diff) patch[c.field] = form[c.field];
      if ('posting_date' in patch) {
        patch.posting_month = fixPostingDate.slice(0, 7);
        patch.fiscal_year = fiscalYearOf(fixPostingDate, fiscal.start_month);
      }
      if ('payment_date' in patch) patch.payment_month = fixPaymentDate.slice(0, 7);
      if ('payment_method' in patch || 'payer' in patch) {
        // 立替型かどうかは支払い方法で決まる(申請時と同じ)
        patch.advance = fixMethod === 'reimbursement';
        const pattern = applicant ? findPattern(patterns, roleOf(applicant), fixMethod, fixPayer || null) : null;
        if (pattern) patch.pattern_id = pattern.id;
      }
      if (fixFiles.length > 0) {
        const added = await uploadFiles(t.id, 'attachments', fixFiles, user.id);
        const before = t.attachments ?? [];
        patch.attachments = [...before, ...added];
        changes.push({
          field: 'attachments',
          label: '添付',
          before: before.length,
          after: before.length + added.length,
          before_text: `${before.length}件`,
          after_text: `${before.length + added.length}件(追加: ${added.map((f) => f.name).join('、')})`,
        });
      }
      const result = await saveApproverEdit({
        transaction: t,
        patch,
        changes,
        userId: user.id,
        feeAccountId: feeAccount?.id ?? null,
      });
      if (!result.historySaved) {
        // 修正そのものは保存済み。履歴だけ残らなかったことを必ず知らせる
        window.alert('修正は保存しましたが、修正履歴の記録に失敗しました。お手数ですが、変更した内容を管理者に伝えてください。');
      }
      reportFlowResult(toast, '修正を保存しました', result);
      setFixing(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
      // ほかの人が先に更新していた場合は、一覧を最新にして閉じる(古い内容のまま直さないように)
      if (isFlowStaleError(e)) {
        onChanged?.();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  /** 修正履歴に残す・表示する値の文字列(IDは名前に直す) */
  function editValueText(field: EditField, value: string | number | null): string {
    if (value === null) return field === 'transfer_fee' ? '未入力' : '(空欄)';
    switch (field) {
      case 'company_id':
        return companyName(String(value));
      case 'department_id':
        return departmentName(String(value));
      case 'hierarchy_node_id': {
        const all = [...nodes, ...fixNodes];
        const n = all.find((x) => x.id === value);
        return n ? toPathLabel(n, all) : String(value);
      }
      case 'account_id':
        return accountName(String(value));
      case 'sub_account_id':
        return subAccountName(String(value));
      case 'amount':
      case 'applied_amount':
      case 'transfer_fee':
        return `${formatYen(Number(value))}円`;
      case 'posting_date':
      case 'payment_date':
      case 'purchase_date':
        return String(value).replace(/-/g, '/');
      case 'payment_method':
        return PAYMENT_METHOD_LABELS[value as PaymentMethod] ?? String(value);
      case 'payer':
        return PAYER_LABELS[value as Exclude<Payer, null>] ?? String(value);
      case 'payment_account_id': {
        const a = cashAccounts.find((x) => x.id === value);
        return a ? formatCashAccountLabel(a, companyName(a.company_id)) : String(value);
      }
      case 'cost_type':
        return COST_TYPE_LABELS[value as CostType] ?? String(value);
      default:
        return String(value);
    }
  }

  const fixLocationOptions = (() => {
    const sites = childrenOf(fixNodes, null);
    return sites.flatMap((site) => [
      { id: site.id, label: site.name },
      ...childrenOf(fixNodes, site.id).map((pl) => ({ id: pl.id, label: `${site.name}/${pl.name}` })),
    ]);
  })();

  return (
    <Modal onClose={onClose} width="min(720px, 94vw)">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {/* 外部システム(Routine など)の確定から作った支払は管理番号が無いため、発生元を出す(2026-10-23) */}
        <h3 style={{ margin: 0 }}>{t.code ?? externalOriginLabel(t.external) ?? ''}</h3>
        <StatusBadge t={t} />
        {/* 報告型は通常の申請と同じ画面に出るため、種別が分かるバッジを付ける(2026-08-27) */}
        {isReport && <span className="badge b-plan">支払い済み申請(承認不要)</span>}
        {t.advance === true && <span className="badge b-pend">立替</span>}
        {/* 経理確認・完了(承認者確認)のボタンは詳細の下部に移動(2026-08-27 指示。承認と同じ配置) */}
        {/* タブ単位ログインのため別タブでは開かず、同じタブで受領書ページへ移動(「戻る」で復帰) */}
        {(t.payment_method === 'prepaid' || t.payment_method === 'cash') && (
          <button
            className="btn-line"
            style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => navigate(`/receipt/${t.id}`)}
          >
            受領書を出力(支払先用)
          </button>
        )}
        {(t.payment_method === 'prepaid' || t.advance === true) && (
          <button
            className="btn-line"
            style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => navigate(`/receipt/${t.id}?mode=internal`)}
          >
            受領書を出力(本人記入用)
          </button>
        )}
        {canFix && (
          <button className="btn-line" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setFixing((f) => !f)}>
            {canFullFix ? '修正(全項目)' : '経理修正'}
          </button>
        )}
      </div>

      {/* 進捗の帯: PC・スマホとも必ず1段(全ステータスを横一列)に収める(2026-08-31 指示)。
          文字は折り返して省略しない */}
      <div
        className="steps-band"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
          // ステータス名・日付・実施者の3行。subgrid で全カードの行位置を揃える
          // (ステータス名が2行になっても日付・名前の高さがズレない。2026-08-31 指示)
          gridTemplateRows: 'auto auto auto',
          gap: isMobile ? 2 : 4,
          padding: isMobile ? '8px 6px' : '10px 12px',
          background: '#FAFBFD',
          border: '1px solid var(--line)',
          borderRadius: 8,
        }}
      >
        {steps.map((s, i) => {
          const done = !!s.at;
          return (
            <div
              key={s.label}
              className="steps-cell"
              title={`${i + 1}. ${s.label}${done ? ` ${tsToShortDate(s.at)} ${name(s.by)}` : ''}`}
              style={{
                minWidth: 0,
                textAlign: 'center',
                padding: isMobile ? '5px 1px' : '6px 2px',
                borderRadius: 6,
                background: done ? 'var(--green-bg)' : '#fff',
                border: `1px solid ${done ? 'var(--green)' : 'var(--line)'}`,
                overflow: 'hidden',
              }}
            >
              {/* 3段構成(2026-08-31 指示): 1行目=ステータス / 2行目=日付 / 3行目=実施者。
                  いずれも省略せず全文を表示する(足りなければ折り返す)。
                  スマホは全ステータスを1段に並べるため文字を小さくする */}
              <div
                style={{
                  fontSize: isMobile ? (steps.length >= 6 ? 9 : 10) : steps.length >= 7 ? 10.5 : 11.5,
                  lineHeight: 1.35,
                  fontWeight: 700,
                  color: done ? 'var(--green)' : 'var(--muted)',
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere',
                }}
              >
                {/* 長いステータス名は意図した位置で改行する(「部門長/確認」「仮払い/(経理)」)。
                    改行しても日付・実施者の行は subgrid で全カード揃う(2026-08-31 指示) */}
                {i + 1}. {isMobile ? s.label.replace(/(確認|\(経理\)|\(完了\)|書添付)$/, '\n$1') : s.label}
              </div>
              <div
                className="hint"
                style={{ fontSize: isMobile ? 8.5 : 10.5, lineHeight: 1.35, whiteSpace: 'normal' }}
              >
                {/* スマホは幅が狭いので曜日を省く(例: 08/28(金)→08/28) */}
                {done ? (isMobile ? tsToShortDate(s.at).replace(/\(.\)$/, '') : tsToShortDate(s.at)) : '—'}
              </div>
              <div
                className="hint"
                style={{ fontSize: isMobile ? 8.5 : 10.5, lineHeight: 1.35, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
              >
                {done ? name(s.by) : ''}
              </div>
            </div>
          );
        })}
      </div>

      {/* 取消(2026-08-26): 申請者本人が取り消した申請。履歴として残る */}
      {t.status === 'cancelled' && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 6,
            background: '#EEF2F8',
            color: 'var(--muted)',
            fontSize: 12.5,
            fontWeight: 700,
          }}
        >
          {tsToDateLabel(t.cancelled_at)} 取消(申請者: {name(t.cancelled_by)})— この申請は取り消されました
        </div>
      )}

      {/* 差戻し: 日時を赤で表示し、コメント履歴を時系列で示す(4章) */}
      {(t.status === 'rejected' || rejectHistory.length > 0) && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 6,
            background: 'var(--red-bg)',
            fontSize: 12.5,
          }}
        >
          {t.status === 'rejected' && (
            <div style={{ color: 'var(--red)', fontWeight: 700 }}>{tsToDateLabel(t.rejected_at)} 差戻し</div>
          )}
          {rejectHistory.length > 0 ? (
            <div style={{ marginTop: 4, display: 'grid', gap: 2 }}>
              {rejectHistory.map((r, i) => (
                <div key={i} style={{ color: 'var(--red)' }}>
                  <span style={{ fontWeight: 600 }}>{tsToDateLabel(r.at)}</span> {name(r.by)}:「{r.reason}」
                </div>
              ))}
            </div>
          ) : (
            t.reject_reason && (
              <div style={{ color: 'var(--red)', marginTop: 2 }}>
                {name(t.rejected_by)}:「{t.reject_reason}」
              </div>
            )
          )}
        </div>
      )}

      {/* 修正履歴(2026-09-16 改修依頼 C): 経理修正・承認者の修正で、いつ・誰が・どの項目を・何から何へ変えたか */}
      {canSeeEditHistory && editHistory.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 6,
            background: '#FAFBFD',
            border: '1px solid var(--line)',
            fontSize: 12.5,
          }}
        >
          <div style={{ fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>修正履歴</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {editHistory.map((e) => (
              <div key={e.id} style={{ overflowWrap: 'anywhere' }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{tsToDateLabel(e.at)}</span> {name(e.by)}
                  <span className="hint">({e.action === 'approver-edit' ? '承認者の修正' : '経理修正'})</span>
                </div>
                {(e.changes ?? []).map((c, i) => (
                  <div key={i} style={{ paddingLeft: 12 }}>
                    {c.label}: {c.before_text} → {c.after_text}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 経理修正フォーム(会社・事業部・拠点・科目の付け替え。「その他」の振替用) */}
      {fixing && (
        <div className="gray-box" style={{ marginTop: 10 }}>
          <div className="box-title">
            {canFullFix
              ? '修正(全項目) — どの段階でも全項目を修正できます。変更は修正履歴に残ります'
              : '経理修正 — 会社・事業部・拠点・科目を付け替えます(金額・日付は変更できません)'}
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div>
              <label>会社</label>
              <select value={fixCompany} onChange={(e) => setFixCompany(e.target.value)}>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>事業部</label>
              <select
                value={fixDept}
                onChange={(e) => {
                  setFixDept(e.target.value);
                  setFixNode('');
                }}
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            {/* 経費申請画面と同じ配置(2026-08-27): 拠点/科目① → 科目②/計上日 → 金額/支払日 →
                支払先 → 摘要 → 備考 → 承認者備考(最下部) */}
            <div>
              <label>拠点/場所</label>
              <select value={fixNode} onChange={(e) => setFixNode(e.target.value)}>
                <option value="">—</option>
                {fixLocationOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>科目①</label>
              <select
                value={fixAccount}
                onChange={(e) => {
                  setFixAccount(e.target.value);
                  setFixSub('');
                }}
              >
                {expenseAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>科目②(任意)</label>
              <select value={fixSub} onChange={(e) => setFixSub(e.target.value)}>
                <option value="">—</option>
                {(fixAccount ? subAccountsOf(fixAccount) : []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {canFullFix && (
              <>
                <div>
                  <label>計上日</label>
                  <DateField value={fixPostingDate} onChange={setFixPostingDate} />
                </div>
                <div>
                  <label>支払い予定金額(税込み・円)</label>
                  <input
                    inputMode="numeric"
                    value={fixAppliedAmount}
                    onChange={(e) => setFixAppliedAmount(formatAmountInput(e.target.value))}
                  />
                </div>
                <div>
                  <label>{isPaid ? '支払日' : '支払い予定日'}</label>
                  <DateField value={fixPaymentDate} onChange={setFixPaymentDate} />
                </div>
                {isPaid && (
                  <div>
                    <label>確定金額(税込み・円)</label>
                    <input
                      inputMode="numeric"
                      value={fixAmount}
                      onChange={(e) => setFixAmount(formatAmountInput(e.target.value))}
                    />
                  </div>
                )}
                {showPurchaseField && (
                  <div>
                    <label>購入日</label>
                    <DateField value={fixPurchaseDate} onChange={setFixPurchaseDate} />
                  </div>
                )}
                {/* 支払い方法・支払い者(2026-09-17)。お金が動いた後は変えない [仮置き] */}
                {methodEditable && (
                  <>
                    <div>
                      <label>支払い方法</label>
                      <select
                        value={fixMethod}
                        disabled={!canChangeMethod}
                        onChange={(e) => setFixMethod(e.target.value as PaymentMethod)}
                      >
                        {!fixMethod && <option value="">—</option>}
                        {[...new Set([...(t.payment_method ? [t.payment_method] : []), ...APPLY_PAYMENT_METHODS])].map((m) => (
                          <option key={m} value={m}>
                            {PAYMENT_METHOD_LABELS[m]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>支払い者</label>
                      <select
                        value={fixPayer}
                        disabled={!canChangeMethod}
                        onChange={(e) => setFixPayer(e.target.value as Exclude<Payer, null>)}
                      >
                        {!fixPayer && <option value="">—</option>}
                        {(['self', 'bucho', 'accounting'] as const).map((p) => (
                          <option key={p} value={p}>
                            {PAYER_LABELS[p]}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!canChangeMethod && (
                      <div className="fw hint" style={{ marginTop: -6 }}>
                        支払い方法・支払い者は、お金が動いた後(仮払い・支払いの後)は変えられません
                      </div>
                    )}
                  </>
                )}
                {showAccountField && (
                  <div>
                    <label>支払い口座</label>
                    <select value={fixPaymentAccountId} onChange={(e) => setFixPaymentAccountId(e.target.value)}>
                      {accountOptions.length === 0 && <option value={fixPaymentAccountId}>読み込み中…</option>}
                      {accountOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {formatCashAccountLabel(a, companyName(a.company_id))}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* 固定費・変動費(2026-09-24 指示)。経費管理表の「固/変」に反映する */}
                <div>
                  <label>固定/変動</label>
                  <select value={fixCostType} onChange={(e) => setFixCostType(e.target.value as CostType)}>
                    {COST_TYPES.map((c) => (
                      <option key={c} value={c}>
                        {COST_TYPE_LABELS[c]}
                      </option>
                    ))}
                  </select>
                </div>
                {showFeeField && (
                  <div>
                    <label>振込手数料(円・空欄は未入力)</label>
                    <input
                      inputMode="numeric"
                      value={fixFee}
                      placeholder="未入力"
                      onChange={(e) => setFixFee(formatAmountInput(e.target.value))}
                    />
                  </div>
                )}
                <div className="fw">
                  <label>添付を追加(領収書/振込明細/請求書)</label>
                  <FileDropZone files={fixFiles} onChange={setFixFiles} />
                </div>
                <div className="fw">
                  <label>支払先</label>
                  <input value={fixCounterparty} onChange={(e) => setFixCounterparty(e.target.value)} />
                </div>
                <div className="fw">
                  <label>摘要(購入品明細、または受領書の場合は但し書き)</label>
                  <input value={fixDescription} onChange={(e) => setFixDescription(e.target.value)} />
                </div>
                <div className="fw">
                  <label>備考</label>
                  <input value={fixNote} onChange={(e) => setFixNote(e.target.value)} />
                </div>
                {/* 承認者備考(2026-08-27 新設・画面最下部) */}
                <div className="fw">
                  <label>承認者備考 — 承認者からの申し送り(詳細に表示されます)</label>
                  <textarea
                    value={fixApproverNote}
                    rows={2}
                    onChange={(e) => setFixApproverNote(e.target.value)}
                    style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13.5, fontFamily: 'inherit' }}
                  />
                </div>
              </>
            )}
          </div>
          {canFullFix && isPaid && (
            <p className="hint" style={{ margin: '8px 0 0' }}>
              支払い後に金額・支払日・支払い口座・振込手数料を変えると、資金繰り(CF)の出金と手数料の計上も合わせて直します。
              完了時に作ったPDF・署名入りの受領書・精算一覧は作り直されません。
            </p>
          )}
          <button className="btn-green" style={{ marginTop: 10 }} disabled={busy} onClick={() => void saveFix()}>
            {busy ? '保存中…' : canFullFix ? '修正を保存' : '経理修正を保存'}
          </button>
        </div>
      )}

      {/* 申請画面と同じ並びで表示(3章)。見出しの補足は削除(2026-08-28 指示) */}
      <Section title="申請内容">
        <div className="two-col">
          <Item label="申請者">{isRecurring ? '繰り返し支払い(自動)' : name(t.created_by)}</Item>
          <Item label="会社">{companyName(t.company_id ?? 'sum')}</Item>
          <Item label="事業部">{departmentName(t.department_id)}</Item>
          <Item label="拠点/場所">{node ? toPathLabel(node, nodes) : '—'}</Item>
          <Item label="科目①">{t.account_id ? accountName(t.account_id) : '(経理入力)'}</Item>
          <Item label="科目②">{t.sub_account_id ? subAccountName(t.sub_account_id) : '—'}</Item>
          <Item label="計上日(PL)">{toShortDate(t.posting_date)}</Item>
          <Item label="支払い予定金額(税込み)">{formatYen(t.applied_amount ?? t.amount)}円</Item>
          <Item label="支払い予定日">{toShortDate(t.payment_date)}</Item>
          <Item label="支払い方法">
            {t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}
            {paymentAccount ? ` / ${paymentAccount}` : ''}
          </Item>
          <Item label="支払い者">
            {t.payer ? PAYER_LABELS[t.payer] : '—'}
            {/* 部門長立替(①②での転換)のときだけ注記。自分の立替は支払い方法「立替」で分かる */}
            {t.advance === true && t.payer === 'bucho' ? '(部門長立替)' : ''}
          </Item>
          {/* 固定費・変動費(2026-09-24 指示)。経費管理表の「固/変」と同じ区分。承認者が修正から切り替える */}
          <Item label="固定/変動">
            {isFixedCost(t) ? '固定' : '変動'}
            {!t.cost_type && <span className="hint">(自動)</span>}
          </Item>
        </div>
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <Item label="領収書/振込明細/請求書" stack={!!t.attachments?.length}>
            {t.attachments?.length ? (
              <AttachmentThumbs files={t.attachments} />
            ) : (
              'なし'
            )}
          </Item>
          <Item label="支払先">{t.counterparty ?? '—'}</Item>
          <Item label="摘要">{t.description ?? '—'}</Item>
          <Item label="備考">{t.note || '—'}</Item>
          {/* 承認者備考(2026-08-27): 完了前の修正で承認者が記入した申し送り */}
          {t.approver_note && <Item label="承認者備考">{t.approver_note}</Item>}
        </div>
      </Section>

      {/* Routine などの確定の明細。Routine の PDF と同じ形(御支払通知書・支払い明細)で出す。表示だけ(2026-10-23) */}
      {t.external && (t.external.detail || t.external.source_system === 'routine') && (
        <Section title="確定の明細(外部システム)">
          <ExternalDetail origin={t.external} detail={t.external.detail} />
        </Section>
      )}

      <Section title="支払いの実績">
        <div className="two-col">
          <Item label="確定金額(税込)">
            {t.paid_at ? <b>{formatYen(t.amount)}円</b> : <span className="hint">(支払い時に確定)</span>}
          </Item>
          {/* クレカ(2026-09-04): 購入日と引き落とし日(=CFの支払日)を分けて表示 */}
          {t.paid_at && t.payment_method === 'card' && t.purchase_date ? (
            <>
              <Item label="購入日">{toShortDate(t.purchase_date)}</Item>
              <Item label="引き落とし日">{toShortDate(t.payment_date)}</Item>
            </>
          ) : (
            <Item label="支払日">{t.paid_at ? toShortDate(t.payment_date) : '—'}</Item>
          )}
          {/* 振込手数料(2026-09-16 改修依頼 E): 未入力のときも項目を必ず表示し、
              「入力されていない」と「表示の不具合」を見分けられるようにする。0円と未入力は区別する */}
          <Item label="振込手数料">
            {t.payment_method !== 'bank_transfer' ? (
              <span className="hint">対象外(振込のときだけ入力します)</span>
            ) : t.transfer_fee == null ? (
              <span className="hint">未入力{t.paid_at ? '' : '(支払い時に入力)'}</span>
            ) : t.transfer_fee === 0 ? (
              '0円'
            ) : (
              `${formatYen(t.transfer_fee)}円(支払手数料として自動計上)`
            )}
          </Item>
          {prepaidFlow && <Item label="受領日">{t.received_at ? tsToDateLabel(t.received_at) : '—(受領待ち)'}</Item>}
          {prepaidFlow && (
            <Item label="受領書(仮払い)" stack={!!t.received_receipts?.length}>
              {t.received_receipts?.length ? (
                <AttachmentThumbs files={t.received_receipts} />
              ) : (
                '—(受領時に添付)'
              )}
            </Item>
          )}
          <Item label="証憑(支払い時)" stack={!!t.receipts?.length}>
            {t.receipts?.length ? (
              <AttachmentThumbs files={t.receipts} />
            ) : (
              'なし'
            )}
          </Item>
          {t.advance === true && (
            <Item label="立替精算">
              {t.reimbursed_at ? (
                <span>
                  精算済み — 精算日 {t.reimbursement_date ? toShortDate(t.reimbursement_date) : '—'} /{' '}
                  {name(t.reimbursed_by)}
                </span>
              ) : (
                <span className="badge b-pend">未精算(経理の精算待ち)</span>
              )}
            </Item>
          )}
          {t.advance === true && (
            <Item label="精算の記録" stack={!!t.settlement_receipts?.length}>
              {t.settlement_receipts?.length ? (
                <>
                  <AttachmentThumbs files={t.settlement_receipts} />
                  <span className="hint">(一覧PDF・受領書/振込明細)</span>
                </>
              ) : t.reimbursed_at ? (
                <span className="badge b-pend">受領書待ち(精算時にサインがありません)</span>
              ) : (
                '—(精算時に作成されます)'
              )}
            </Item>
          )}
        </div>
      </Section>

      {/* 支払い済み申請の確認操作(2026-08-27: 承認と同様に詳細の下部へ配置)。
          経理確認する=経理・管理者 / 完了する(ロック)=承認者(経理確認が済むまで押せない) */}
      {(canReportAccounting || canReportApprover) && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid var(--line)',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {canReportAccounting && (
            <button className="btn-sm-ok" disabled={busy} onClick={() => void runReportConfirm('accounting')}>
              経理確認する
            </button>
          )}
          {canReportApprover && (
            <button
              className="btn-sm-ok"
              // 立替の報告は経理の精算(受領のサインまたは振込明細)が済むまで完了できない(ルールと同じ条件。2026-09-09 総点検)
              disabled={busy || !t.report_accounting_confirmed_at || !reportSettled}
              title={
                !t.report_accounting_confirmed_at
                  ? '先に経理確認が必要です'
                  : !reportSettled
                    ? '経理の精算(受領のサインまたは振込明細)が済むと完了できます'
                    : undefined
              }
              onClick={() => void runReportConfirm('approver')}
            >
              完了する(ロック)
            </button>
          )}
          <span className="hint">報告 → 経理確認 → 完了(承認者確認)の順に進みます</span>
        </div>
      )}

      {actions && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--line)' }}>{actions}</div>
      )}
    </Modal>
  );
}
